import type { Achievement, AchievementTier, MemberHistory } from './achievements.types.js';

export interface AchievementSpec {
  code: string;
  tier?: AchievementTier;
  /** Pure detection function. Returns the badge or null. */
  evaluate: (h: MemberHistory) => Achievement | null;
}

function sessionsSpec(count: number, tier?: AchievementTier): AchievementSpec {
  const code = count === 1 ? 'first_session' : `sessions_${count}`;
  return {
    code,
    ...(tier ? { tier } : {}),
    evaluate(h) {
      const at = h.sessionAchievedAt.get(count);
      if (!at) return null;
      return { code, achievedAt: at, ...(tier ? { tier } : {}) };
    },
  };
}

function streakSpec(threshold: number, tier?: AchievementTier): AchievementSpec {
  const code = `streak_${threshold}`;
  return {
    code,
    ...(tier ? { tier } : {}),
    evaluate(h) {
      const at = h.tenStreakAchievedAt.get(threshold);
      if (!at) return null;
      return { code, achievedAt: at, ...(tier ? { tier } : {}) };
    },
  };
}

function hitFactorSpec(threshold: number, tier?: AchievementTier): AchievementSpec {
  const code = `hit_factor_${threshold}`;
  return {
    code,
    ...(tier ? { tier } : {}),
    evaluate(h) {
      const at = h.hitFactorAchievedAt.get(threshold);
      if (!at) return null;
      return { code, achievedAt: at, ...(tier ? { tier } : {}) };
    },
  };
}

/**
 * The catalogue is a flat list. Each entry is independent; a member
 * unlocks any subset of these on a per-detection basis.
 *
 * Tier convention: bronze ≈ "a real milestone", silver ≈ "you put in
 * the years", gold ≈ "deeply uncommon". Tier-less entries are flat
 * achievements (no progression bar).
 *
 * Adding a new entry: append below + add a fixture case in
 * `achievements.test.ts`. The catalogue is consumed by `evaluate()`
 * and rendered by `AchievementsTab` in `apps/web/src/app/me/statistics`.
 */
export const ACHIEVEMENTS: ReadonlyArray<AchievementSpec> = [
  // Session-count progression.
  sessionsSpec(1),
  sessionsSpec(10, 'bronze'),
  sessionsSpec(50, 'bronze'),
  sessionsSpec(100, 'silver'),
  sessionsSpec(250, 'silver'),
  sessionsSpec(500, 'gold'),

  // Streaks of consecutive 10s in a ring round.
  streakSpec(10, 'bronze'),
  streakSpec(20, 'silver'),
  streakSpec(50, 'gold'),

  // IPSC hit-factor thresholds.
  hitFactorSpec(5, 'bronze'),
  hitFactorSpec(7, 'silver'),
  hitFactorSpec(9, 'gold'),

  // ÖSB Schnellfeuer-Vorübung — 540 raw on the standard 60-shot programme.
  {
    code: 'osb_schnellfeuer_540',
    tier: 'silver',
    evaluate(h) {
      if (!h.bestOsbSchnellfeuerVorubung) return null;
      if (h.bestOsbSchnellfeuerVorubung.value < 540) return null;
      return {
        code: 'osb_schnellfeuer_540',
        tier: 'silver',
        achievedAt: h.bestOsbSchnellfeuerVorubung.achievedAt,
      };
    },
  },

  // Breadth: shot in three different disciplines.
  {
    code: 'three_disciplines',
    tier: 'bronze',
    evaluate(h) {
      if (!h.threeDisciplinesAchievedAt) return null;
      return {
        code: 'three_disciplines',
        tier: 'bronze',
        achievedAt: h.threeDisciplinesAchievedAt,
      };
    },
  },

  // Breadth: shot at three different ranges.
  {
    code: 'three_locations',
    tier: 'bronze',
    evaluate(h) {
      if (!h.threeLocationsAchievedAt) return null;
      return {
        code: 'three_locations',
        tier: 'bronze',
        achievedAt: h.threeLocationsAchievedAt,
      };
    },
  },

  // Clean season: a 12-month rolling window with ≥ 12 sessions and
  // zero penalties recorded.
  {
    code: 'clean_season',
    tier: 'silver',
    evaluate(h) {
      if (!h.cleanSeasonAchievedAt) return null;
      return {
        code: 'clean_season',
        tier: 'silver',
        achievedAt: h.cleanSeasonAchievedAt,
      };
    },
  },
];
